import { useEffect, useState, useMemo } from "react";
import { Box } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import { ImSpinner8 } from "react-icons/im";
import DisplayAmount from "../../../../views/admin/shared/DisplayAmount";

ModuleRegistry.registerModules([AllCommunityModule]);

type CommissionRow = {
  asset: string;
  available: string;
  pending: string;
  crypto: boolean;
  thisMonth: string;
  lastMonth: string;
};

type Props = {
  data: CommissionRow[];
  isFetchingCommission: boolean;
};

const AgentCommissionTab = ({ data, isFetchingCommission }: Props) => {
  const [fiatData, setFiatData] = useState<CommissionRow[]>([]);
  const [cryptoData, setCryptoData] = useState<CommissionRow[]>([]);

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const fiat = data.filter((item: CommissionRow) => !item.crypto);
      const crypto = data.filter((item: CommissionRow) => item.crypto);

      setFiatData(fiat);
      setCryptoData(crypto);
    }
  }, [data]);

  const AvailableRenderer = (params: ICellRendererParams) => (
    <DisplayAmount
      amount={Number(params.data.available)}
      currency={params.data.asset}
    />
  );

  const ThisMonthRenderer = (params: ICellRendererParams) => (
    <DisplayAmount
      amount={Number(params.data.thisMonth)}
      currency={params.data.asset}
    />
  );

  const LastMonthRenderer = (params: ICellRendererParams) => (
    <DisplayAmount
      amount={Number(params.data.lastMonth)}
      currency={params.data.asset}
    />
  );

  const columns: ColDef[] = [
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      flex: 1,
    },
    {
      field: "available",
      headerName: "Available",
      sortable: false,
      flex: 1,
      cellRenderer: AvailableRenderer,
    },
    {
      field: "thisMonth",
      headerName: "This Month",
      sortable: false,
      flex: 1,
      cellRenderer: ThisMonthRenderer,
    },
    {
      field: "lastMonth",
      headerName: "Last Month",
      sortable: false,
      flex: 1,
      cellRenderer: LastMonthRenderer,
    },
  ];

  const columnsCrypto: ColDef[] = [
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      flex: 1,
    },
    {
      field: "available",
      headerName: "Available",
      sortable: false,
      flex: 1,
    },
    {
      field: "thisMonth",
      headerName: "This Month",
      sortable: false,
      flex: 1,
    },
    {
      field: "lastMonth",
      headerName: "Last Month",
      sortable: false,
      flex: 1,
    },
  ];

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  if (isFetchingCommission || !data) {
    return (
      <div className="flex min-h-[200px] items-center justify-center p-7">
        <ImSpinner8
          className="spinner mt-1 text-primary-500"
          style={{ fontSize: "1.8rem" }}
        />
      </div>
    );
  }

  if (
    !isFetchingCommission &&
    (!fiatData || fiatData.length === 0) &&
    (!cryptoData || cryptoData.length === 0)
  ) {
    return (
      <div className="p-7">
        <p className="py-8 text-center text-sm text-gray-500">
          No commission earned
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 py-8">
      {fiatData && fiatData.length > 0 && (
        <Box mb={4}>
          <div className="mb-2 text-sm font-semibold text-navy-700">
            Fiat Commission
          </div>
          <div style={{ width: "100%" }}>
            <AgGridReact
              theme={themeAlpine}
              rowData={fiatData}
              columnDefs={columns}
              defaultColDef={defaultColDef}
              getRowId={(params) => params.data.asset}
              rowHeight={48}
              headerHeight={44}
              domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
              suppressPaginationPanel={true}
              overlayNoRowsTemplate="No data available"
            />
          </div>
        </Box>
      )}
      {cryptoData && cryptoData.length > 0 && (
        <Box>
          <div className="mb-2 text-sm font-semibold text-navy-700">
            Wallet Commission
          </div>
          <div style={{ width: "100%" }}>
            <AgGridReact
              theme={themeAlpine}
              rowData={cryptoData}
              columnDefs={columnsCrypto}
              defaultColDef={defaultColDef}
              getRowId={(params) => params.data.asset}
              rowHeight={48}
              headerHeight={44}
              domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
              suppressPaginationPanel={true}
              overlayNoRowsTemplate="No data available"
            />
          </div>
        </Box>
      )}
    </div>
  );
};

export default AgentCommissionTab;
